/**
 * Theatre map (08 §2.2, 04 §2.2): the current theatre's node graph, the company rail, the
 * operation's vital numbers, and the card of whichever node has focus. Mission and rescue nodes
 * lead to the briefing; support nodes resolve in a dialog; withdrawing asks first (08 §5.3).
 */

import './theatre.css';
import type { NodeKind, Operation, TheatreNode } from '../../../meta/types';
import { archetypeNumber } from '../archetypes';
import type { Ctx, Screen } from '../context';
import { box, button, el, errorMessage, hinted, panel } from '../dom';
import { difficultyName, RP_MEANING } from '../text';
import { confirm, notice } from '../widgets/confirm';
import { frame } from '../widgets/frame';
import { segmentGauge } from '../widgets/gauge';
import { companyRail } from './companyRail';
import { currentColumn, currentTheatre, isChosenEdge, layoutGraph, theatreNodes } from './graphLayout';
import { hqAvailable, hqLabel, openHqDialog } from './hqDialog';
import type { NodeCard } from './nodeCard';
import { isMissionKind, nodeCard, nodeName, STATE_LABEL } from './nodeCard';
import { openSupportDialog } from './supportDialog';

/** The graph's drawing area in canvas pixels; the panel around it scrolls if text size squeezes it. */
export const MAP_W = 334;
export const MAP_H = 206;
const NODE_HALF = 10;
const SVG_NS = 'http://www.w3.org/2000/svg';

/** Support nodes show a letter; missions show the manual's mission number. */
const KIND_SYMBOL: Readonly<Record<NodeKind, string>> = {
  mission: 'M',
  rest: 'R',
  supply: 'S',
  depot: 'D',
  intercept: 'I',
  rescue: 'P',
};

/** A mark beside the symbol so state never rides on colour alone (08 §4). */
const STATE_MARK: Readonly<Record<TheatreNode['state'], string>> = {
  hidden: '?',
  scouted: '',
  open: '>',
  done: 'OK',
  failed: 'X',
  skipped: '-',
};

/** Not yet flown or passed by: the only nodes a dynamic marker still matters on. */
function isUnresolved(node: TheatreNode): boolean {
  return node.state === 'hidden' || node.state === 'scouted' || node.state === 'open';
}

function symbolOf(node: TheatreNode): string {
  if (node.kind === 'mission' && node.params !== null) return archetypeNumber(node.params.archetype);
  return KIND_SYMBOL[node.kind];
}

function noOperation(ctx: Ctx, op: Operation | null): Screen {
  const f = frame('theatre', 'THEATRE MAP');
  const toTitle = button('TITLE', () => ctx.nav.title());
  const actions = box('menu-row', toTitle);
  // A finished run still owes the player its report (and, after a win, the induction).
  if (op !== null) actions.append(button('OPERATION REPORT', () => ctx.nav.operationEnd()));
  f.body.append(box('menu-col menu-theatre-empty', el('div', 'menu-head-title', 'NO ACTIVE OPERATION'), actions));
  return { el: f.el, back: () => ctx.nav.title(), initialFocus: () => toTitle };
}

interface GraphParts {
  el: HTMLElement;
  firstOpen: HTMLButtonElement | null;
}

function buildGraph(ctx: Ctx, op: Operation, nodes: TheatreNode[], open: TheatreNode[], card: NodeCard): GraphParts {
  const layout = layoutGraph(nodes, MAP_W, MAP_H, 22);
  const reachable = new Set(open.map((n) => n.id));
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const graph = el('div', 'menu-graph');
  graph.style.width = `${MAP_W}px`;
  graph.style.height = `${MAP_H}px`;

  const column = currentColumn(open, nodes[0]?.theatre ?? 0);
  const inColumn = nodes.find((n) => n.column === column);
  const bandAt = inColumn === undefined ? undefined : layout.points.get(inColumn.id);
  if (bandAt !== undefined) {
    const band = el('div', 'menu-graph-band');
    band.style.left = `${bandAt.x - 17}px`;
    graph.append(band);
  }

  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', 'menu-links');
  svg.setAttribute('width', String(MAP_W));
  svg.setAttribute('height', String(MAP_H));
  svg.setAttribute('viewBox', `0 0 ${MAP_W} ${MAP_H}`);
  svg.setAttribute('aria-hidden', 'true');
  for (const edge of layout.edges) {
    const from = byId.get(edge.from);
    const to = byId.get(edge.to);
    const line = document.createElementNS(SVG_NS, 'line');
    const chosen = from !== undefined && to !== undefined && isChosenEdge(from, to, op.position);
    line.setAttribute('class', chosen ? 'menu-link menu-link-chosen' : 'menu-link');
    line.setAttribute('x1', String(edge.x1));
    line.setAttribute('y1', String(edge.y1));
    line.setAttribute('x2', String(edge.x2));
    line.setAttribute('y2', String(edge.y2));
    svg.append(line);
  }
  graph.append(svg);

  let firstOpen: HTMLButtonElement | null = null;
  for (const node of nodes) {
    const at = layout.points.get(node.id);
    if (at === undefined) continue;
    const canGo = reachable.has(node.id);
    const hint = !canGo ? '' : isMissionKind(node.kind) ? 'ENTER: READ THE BRIEFING' : 'ENTER: RESOLVE THIS NODE';
    const b = button('', () => activate(ctx, node, canGo, card), `menu-node-${node.kind} menu-node-${node.state}`);
    // A node is a button, but not a text button: it wears its own class instead of `menu-btn`.
    b.classList.remove('menu-btn');
    b.classList.add('menu-node');
    b.classList.toggle('menu-node-keystone', node.keystone);
    // Tag Recovery is a dynamic node (08 §2.2): it pulses, and wears a T so the pulse is not the only sign.
    const tags = node.tagRecovery !== undefined && isUnresolved(node);
    b.classList.toggle('menu-node-tag', tags);
    b.style.left = `${at.x - NODE_HALF}px`;
    b.style.top = `${at.y - NODE_HALF}px`;
    b.append(el('span', 'menu-node-symbol', symbolOf(node)));
    if (STATE_MARK[node.state] !== '') b.append(el('span', 'menu-node-mark', STATE_MARK[node.state]));
    if (tags) b.append(el('span', 'menu-node-mark menu-node-mark-tag', 'T'));
    const key = `${node.keystone ? ', KEYSTONE' : ''}${tags ? ', TAG RECOVERY' : ''}`;
    b.setAttribute('aria-label', `${nodeName(ctx, node)}: ${STATE_LABEL[node.state]}${key}`);
    if (!canGo) b.setAttribute('aria-disabled', 'true');
    b.addEventListener('focus', () => card.show(node, hint));
    b.addEventListener('mouseenter', () => card.show(node, hint));
    graph.append(b);
    if (node.keystone) {
      const label = el('span', 'menu-node-keylabel', 'KEYSTONE');
      label.style.left = `${at.x - 24}px`;
      label.style.top = `${at.y + NODE_HALF + 3}px`;
      graph.append(label);
    }
    if (canGo) firstOpen ??= b;
  }
  return { el: graph, firstOpen };
}

function activate(ctx: Ctx, node: TheatreNode, canGo: boolean, card: NodeCard): void {
  if (!canGo) {
    card.show(node, 'NOT REACHABLE FROM HERE');
    return;
  }
  if (!isMissionKind(node.kind)) {
    openSupportDialog(ctx, node, () => ctx.nav.theatre());
    return;
  }
  if (node.params === null) {
    card.show(node, 'NO MISSION ORDERS FOR THIS NODE');
    return;
  }
  ctx.nav.briefing({ mode: 'operation', params: node.params, node, rangerId: null });
}

function legend(ctx: Ctx, nodes: readonly TheatreNode[]): HTMLElement {
  const row = el('div', 'menu-legend menu-small');
  const kinds = [...new Set(nodes.map((n) => n.kind))];
  for (const kind of kinds) {
    const symbol = kind === 'mission' ? '01-12' : KIND_SYMBOL[kind];
    const name = el('span', 'menu-dim', ctx.describe(kind).name);
    row.append(box('menu-legend-item', el('span', 'menu-paper', symbol), name));
  }
  return row;
}

/** What withdrawing now would cost beyond the operation itself: captives still held are lost with it. Pure. */
export function withdrawalCosts(op: Operation): string[] {
  const held = op.company.filter((r) => r.status === 'pow').length;
  if (held === 0) return [];
  return [held === 1
    ? 'ONE CAPTURED RANGER IS STILL HELD. HE WILL NEVER BE RECOVERED.'
    : `${held} CAPTURED RANGERS ARE STILL HELD. THEY WILL NEVER BE RECOVERED.`];
}

function withdraw(ctx: Ctx, op: Operation): void {
  confirm(ctx, {
    title: 'WITHDRAW OPERATION',
    lines: [
      `END OPERATION ${op.codename} HERE?`,
      'SURVIVORS ARE NOT INDUCTED INTO THE HALL OF HONOR. UNLOCK PROGRESS IS KEPT.',
      ...withdrawalCosts(op),
      'THIS CANNOT BE UNDONE.',
    ],
    confirmLabel: 'WITHDRAW',
    cancelLabel: 'STAY IN THE FIGHT',
    danger: true,
    onConfirm: () => {
      try {
        ctx.api.withdrawOperation();
      } catch (err) {
        notice(ctx, 'WITHDRAWAL REFUSED', [errorMessage(err)]);
        return;
      }
      ctx.nav.title();
    },
  });
}

export function theatreMapScreen(ctx: Ctx): Screen {
  const op = ctx.api.profile().operation;
  if (op === null || op.status !== 'active') return noOperation(ctx, op);
  const open = ctx.api.openNodes();
  const theatre = currentTheatre(op, open);
  const nodes = theatreNodes(op, theatre);

  const f = frame('theatre', 'THEATRE MAP');
  const readiness = segmentGauge('READINESS', 10, 6);
  readiness.set(op.readiness);
  const level = `${difficultyName(op.difficulty)} / HAZARD ${op.hazard}`;
  const codename = el('span', 'menu-paper', op.codename);
  f.info.append(codename, hinted('', `RP ${op.rp}`, RP_MEANING), readiness.el, el('span', '', level));
  if (op.cadet) f.info.append(el('span', 'menu-chip menu-chip-dim', 'CADET'));

  const rail = panel('COMPANY', 'menu-theatre-rail');
  rail.body.append(companyRail(ctx, op.company));

  const card = nodeCard(ctx);
  const biome = op.theatres[theatre];
  const where = biome === undefined ? '' : ` - ${ctx.describe(biome).name}`;
  const map = panel(`THEATRE ${theatre + 1} OF ${op.theatres.length}${where}`, 'menu-theatre-map menu-grow');
  const graph = buildGraph(ctx, op, nodes, open, card);
  map.body.append(graph.el, legend(ctx, nodes));

  const log = panel('OPERATIONS LOG', 'menu-theatre-log');
  const lines = op.log.slice(-3);
  for (const line of lines) log.body.append(el('div', 'menu-prose menu-paper', line));
  if (lines.length === 0) log.body.append(el('div', 'menu-dim', 'NOTHING TO REPORT'));

  const cardPanel = panel('NODE', 'menu-theatre-card menu-grow');
  cardPanel.body.append(card.el);
  const toTitle = button('TITLE', () => ctx.nav.title());
  const actions = box('menu-theatre-actions menu-col');
  if (hqAvailable(ctx, op)) {
    actions.append(button(hqLabel(op), () => openHqDialog(ctx, op, () => ctx.nav.theatre())));
  }
  actions.append(button('WITHDRAW OPERATION', () => withdraw(ctx, op), 'menu-btn-danger'), toTitle);

  f.body.append(
    rail.panel,
    box('menu-col menu-grow', map.panel, log.panel),
    box('menu-col menu-theatre-side', cardPanel.panel, actions),
  );
  return {
    el: f.el,
    back: () => ctx.nav.title(),
    initialFocus: () => graph.firstOpen ?? toTitle,
  };
}
